import { createReducer } from '@reduxjs/toolkit'
import { castDraft } from 'immer'
import { Reducer } from 'redux'

import { BrandFanFilters } from '@enums'
import { AuthActionTypes } from '@store/auth'
import {
  AddBrandFanGroupMemberActionTypes,
  BrandFansActionTypes,
  CreateBrandFanGroupActionTypes,
  DeleteBrandFanGroupActionTypes,
  FetchBrandFanGroupMembersActionTypes,
  fetchBrandFanGroups,
  fetchBrandFanGroupsError,
  fetchBrandFanGroupsSuccess,
  FetchInfluencerIdentitiesActionTypes,
  FetchInfluencerIdentityActionTypes,
  FetchInfluencerIdentitySubmissionsActionTypes,
  IBrandFanGroup,
  IBrandFanMember,
  IBrandFansState,
  IInfluencerIdentity,
  RemoveBrandFanGroupMemberActionTypes,
  UpdateBrandFanGroupActionTypes,
} from '@store/brandFans'
import { fetchBrandFansMembershipSuccess } from '@store/submissions/actions/fetchSubmissionBrandFanMembership'
import { addToSet, appendToSet, removeFromSet, returnSecondIfDeepEqual } from '@utils'
import { indexById } from '@utils/groupBy'
import { toggleIdentity } from './actions/open'
import { removeBrandFanMember } from './actions/removeBrandFanMember'

const initialState: IBrandFansState = {
  isFetchingIdentities: false,
  isFetchingBrandFanGroups: false,
  isUpdatingBrandFanGroup: false,
  isCreatingBrandFanGroup: false,
  identitiesById: {},
  identityIdsByFilter: {},
  groupsById: {},
  groupIds: [],
  identityIdsByGroupId: {},
  stats: {},
  linksByFilter: {},
  membersLinksByGroupId: {},
  submissionsByIdentityId: {},
  pendingRequestsByInfluencerIdentityId: [],
  pendingRequestsByBrandFanGroupId: [],
  openedIdentities: {},
  membersByGroupId: {},
  membershipById: {},
  membersByInfluencerId: {},
  membersByIdentityId: {},
  errors: undefined,
}

const reducer: Reducer<IBrandFansState> = (state = initialState, action) => {
  switch (action.type) {
    case FetchBrandFanGroupMembersActionTypes.FETCH_REQUEST:
    case FetchInfluencerIdentitiesActionTypes.FETCH_REQUEST: {
      return {
        ...state,
        isFetchingIdentities: true,
      }
    }

    case FetchInfluencerIdentitiesActionTypes.RESET_LIST: {
      const filter = action.payload
      return {
        ...state,
        identityIdsByFilter: {
          ...state.identityIdsByFilter,
          [filter]: [],
        },
      }
    }

    case UpdateBrandFanGroupActionTypes.UPDATE_REQUEST: {
      return {
        ...state,
        isUpdatingBrandFanGroup: true,
      }
    }

    case DeleteBrandFanGroupActionTypes.DELETE_REQUEST: {
      return {
        ...state,
        isUpdatingBrandFanGroup: true,
      }
    }

    case CreateBrandFanGroupActionTypes.CREATE_REQUEST: {
      return {
        ...state,
        isCreatingBrandFanGroup: true,
      }
    }

    case RemoveBrandFanGroupMemberActionTypes.REMOVE_MEMBER_REQUEST:
    case AddBrandFanGroupMemberActionTypes.ADD_MEMBER_REQUEST: {
      const { groupId } = action.payload
      return {
        ...state,
        pendingRequestsByBrandFanGroupId: addToSet(state.pendingRequestsByBrandFanGroupId, [
          groupId,
        ]),
      }
    }

    case UpdateBrandFanGroupActionTypes.UPDATE_SUCCESS: {
      const brandFanGroup: IBrandFanGroup = action.payload
      return {
        ...state,
        isUpdatingBrandFanGroup: false,
        groupsById: {
          ...state.groupsById,
          [brandFanGroup.id]: brandFanGroup,
        },
      }
    }

    case DeleteBrandFanGroupActionTypes.DELETE_SUCCESS: {
      const { groupId } = action.payload
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [groupId]: _deletedGroup, ...remainingGroups } = state.groupsById
      return {
        ...state,
        isUpdatingBrandFanGroup: false,
        groupsById: remainingGroups,
        groupIds: state.groupIds.filter((id) => id !== groupId),
      }
    }

    case FetchInfluencerIdentitySubmissionsActionTypes.FETCH_REQUEST: {
      const identityId = action.payload
      return {
        ...state,
        pendingRequestsByInfluencerIdentityId: addToSet(
          state.pendingRequestsByInfluencerIdentityId,
          [identityId],
        ),
      }
    }

    case FetchInfluencerIdentitiesActionTypes.FETCH_SUCCESS: {
      const { influencerIdentities, metadata, filter, links, mergeType } = action.payload
      const identityIdsForCurrentFilter = state.identityIdsByFilter[filter] || []
      const mergeFunction = mergeType === 'append' ? appendToSet : addToSet
      return {
        ...state,
        isFetchingIdentities: false,
        identitiesById: returnSecondIfDeepEqual(
          indexById(influencerIdentities, state.identitiesById),
          state.identitiesById,
        ),
        identityIdsByFilter: {
          ...state.identityIdsByFilter,
          [filter]: mergeFunction(
            identityIdsForCurrentFilter,
            influencerIdentities.map((identity) => identity.id),
          ),
        },
        linksByFilter: {
          ...state.linksByFilter,
          [filter]: returnSecondIfDeepEqual(links, state.linksByFilter[filter]),
        },
        stats: {
          ...state.stats,
          ...metadata.stats,
        },
      }
    }

    case FetchInfluencerIdentityActionTypes.FETCH_SUCCESS: {
      const influencerIdentity: IInfluencerIdentity = action.payload
      const hasNoGroupMembership = !influencerIdentity.membership_group_ids.length
      const stateBrandFanFilterGroupIds = state.identityIdsByFilter[BrandFanFilters.BrandFans]
      return {
        ...state,
        identitiesById: returnSecondIfDeepEqual(
          {
            ...state.identitiesById,
            [influencerIdentity.id]: influencerIdentity,
          },
          state.identitiesById,
        ),
        identityIdsByFilter: {
          ...state.identityIdsByFilter,
          [BrandFanFilters.BrandFans]: hasNoGroupMembership
            ? removeFromSet(stateBrandFanFilterGroupIds, influencerIdentity.id)
            : stateBrandFanFilterGroupIds,
        },
      }
    }

    case CreateBrandFanGroupActionTypes.CREATE_SUCCESS: {
      const brandFanGroup = action.payload
      return {
        ...state,
        isCreatingBrandFanGroup: false,
        groupsById: {
          ...state.groupsById,
          [brandFanGroup.id]: brandFanGroup,
        },
        groupIds: addToSet(state.groupIds, [brandFanGroup.id]),
      }
    }

    case FetchInfluencerIdentitySubmissionsActionTypes.FETCH_SUCCESS: {
      const { identityId, submissions } = action.payload
      return {
        ...state,
        submissionsByIdentityId: returnSecondIfDeepEqual(
          {
            ...state.submissionsByIdentityId,
            [identityId]: submissions,
          },
          state.submissionsByIdentityId,
        ),
        pendingRequestsByInfluencerIdentityId: removeFromSet(
          state.pendingRequestsByInfluencerIdentityId,
          identityId,
        ),
      }
    }

    case FetchBrandFanGroupMembersActionTypes.RESET_LIST: {
      const groupId = action.payload
      return {
        ...state,
        identityIdsByGroupId: {
          ...state.identityIdsByGroupId,
          [groupId]: [],
        },
      }
    }

    case FetchBrandFanGroupMembersActionTypes.FETCH_SUCCESS: {
      const { groupId, brandFanGroupMembers, links } = action.payload
      const identityIdsForCurrentGroupId = state.identityIdsByGroupId[groupId] || []
      const groupMemberIdentityIds = brandFanGroupMembers.map((member) => member.identity_id)

      const addToMapList = <T extends IBrandFanMember, U extends string | number>(
        items: Array<T>,
        map: Record<U, ReadonlyArray<T>>,
        keyName: string,
      ) => {
        return items.reduce(
          (acc, item) => {
            const keyId = item[keyName]
            if (!acc[keyId]) {
              acc[keyId] = []
            }
            acc[keyId] = acc[keyId].filter((a) => a.id !== item.id)
            acc[keyId].push(item)
            return acc
          },
          { ...map },
        )
      }

      return {
        ...state,
        isFetchingIdentities: false,
        identitiesById: returnSecondIfDeepEqual(
          indexById(brandFanGroupMembers, state.identitiesById),
          state.identitiesById,
        ),
        identityIdsByGroupId: {
          ...state.identityIdsByGroupId,
          [groupId]: returnSecondIfDeepEqual(
            appendToSet(identityIdsForCurrentGroupId, groupMemberIdentityIds),
            identityIdsForCurrentGroupId,
          ),
        },
        membersLinksByGroupId: {
          ...state.membersLinksByGroupId,
          [groupId]: returnSecondIfDeepEqual(links, state.membersLinksByGroupId),
        },
        membersByGroupId: {
          ...state.membersByGroupId,
          [groupId]: returnSecondIfDeepEqual(
            indexById(brandFanGroupMembers, state.membersByGroupId[groupId]),
            state.membersByGroupId[groupId],
          ),
        },
        membershipById: {
          ...state.membershipById,
          ...indexById(brandFanGroupMembers, state.membershipById),
        },
        membersByIdentityId: addToMapList(
          brandFanGroupMembers,
          state.membersByIdentityId,
          'identity_id',
        ),
        membersByInfluencerId: addToMapList(
          brandFanGroupMembers,
          state.membersByInfluencerId,
          'influencer_id',
        ),
      }
    }

    case AddBrandFanGroupMemberActionTypes.ADD_MEMBER_SUCCESS: {
      const { groupId } = action.payload
      return {
        ...state,
        pendingRequestsByBrandFanGroupId: removeFromSet(
          state.pendingRequestsByBrandFanGroupId,
          groupId,
        ),
        groupsById: {
          ...state.groupsById,
          [groupId]: {
            ...state.groupsById[groupId],
            member_count: state.groupsById[groupId].member_count + 1,
          },
        },
      }
    }

    case RemoveBrandFanGroupMemberActionTypes.REMOVE_MEMBER_SUCCESS: {
      const { identityId, groupId } = action.payload

      return {
        ...state,
        pendingRequestsByBrandFanGroupId: removeFromSet(
          state.pendingRequestsByBrandFanGroupId,
          groupId,
        ),
        identityIdsByGroupId: {
          ...state.identityIdsByGroupId,
          [groupId]: removeFromSet(state.identityIdsByGroupId[groupId], identityId),
        },
        groupsById: {
          ...state.groupsById,
          [groupId]: {
            ...state.groupsById[groupId],
            member_count: state.groupsById[groupId].member_count - 1,
          },
        },
        membersByGroupId: {
          ...state.membersByGroupId,
          [groupId]: {
            ...Object.values(state.membersByGroupId[groupId] ?? {}).reduce(
              (acc, member: IBrandFanMember) => {
                if (member.identity_id !== identityId) {
                  acc[member.id] = member
                }
                return acc
              },
              {},
            ),
          },
        },
      }
    }

    case FetchBrandFanGroupMembersActionTypes.FETCH_ERROR:
    case RemoveBrandFanGroupMemberActionTypes.REMOVE_MEMBER_ERROR:
    case AddBrandFanGroupMemberActionTypes.ADD_MEMBER_ERROR:
    case DeleteBrandFanGroupActionTypes.DELETE_ERROR:
    case UpdateBrandFanGroupActionTypes.UPDATE_ERROR:
    case CreateBrandFanGroupActionTypes.CREATE_ERROR:
    case FetchInfluencerIdentitiesActionTypes.FETCH_ERROR:
    case FetchInfluencerIdentityActionTypes.FETCH_ERROR: {
      return {
        ...state,
        isFetchingIdentities: false,
        isFetchingBrandFanGroups: false,
        isUpdatingBrandFanGroup: false,
        isCreatingBrandFanGroup: false,
        errors: action.payload,
      }
    }

    case FetchInfluencerIdentitySubmissionsActionTypes.FETCH_ERROR: {
      const { identityId, errors } = action.payload
      return {
        ...state,
        pendingRequestsByInfluencerIdentityId: removeFromSet(
          state.pendingRequestsByInfluencerIdentityId,
          identityId,
        ),
        errors,
      }
    }

    case BrandFansActionTypes.CLEAR_ERRORS: {
      return {
        ...state,
        errors: undefined,
      }
    }

    case AuthActionTypes.SIGNOUT_SUCCESS:
      return initialState

    default:
      return state
  }
}

const brandFansReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(toggleIdentity, (state, { payload: identityId }) => {
      state.openedIdentities[identityId] = !state.openedIdentities[identityId]
    })
    .addCase(fetchBrandFansMembershipSuccess, (state, action) => {
      const { brandFanMembers } = action.payload
      brandFanMembers.forEach((member) => {
        const draftMember = castDraft(member)
        state.membershipById[member.id] = draftMember
        state.membersByGroupId[member.brand_fan_group_id] = {
          ...state.membersByGroupId[member.brand_fan_group_id],
        }
        state.membersByGroupId[member.brand_fan_group_id][member.id] = draftMember
        const influencerMemberships = state.membersByInfluencerId[member.influencer_id] ?? []
        if (
          !influencerMemberships.some((existingMembership) => existingMembership.id === member.id)
        ) {
          state.membersByInfluencerId[member.influencer_id] = [
            ...influencerMemberships,
            draftMember,
          ]
        }

        const identityMemberships = state.membersByIdentityId[member.identity_id] ?? []
        if (
          !identityMemberships.some((existingMembership) => existingMembership.id === member.id)
        ) {
          state.membersByIdentityId[member.identity_id] = [...identityMemberships, draftMember]
        }
      })
    })
    .addCase(removeBrandFanMember, (state, { payload: { groupId, memberId } }) => {
      const membership = state.membersByGroupId[groupId][memberId]
      if (state.membersByInfluencerId[membership.influencer_id]) {
        state.membersByInfluencerId[membership.influencer_id] = state.membersByInfluencerId[
          membership.influencer_id
        ].filter((member) => member.id !== memberId)
      }

      if (state.membersByIdentityId[membership.identity_id]) {
        state.membersByIdentityId[membership.identity_id] = state.membersByIdentityId[
          membership.identity_id
        ].filter((member) => member.id !== memberId)
      }

      delete state.membershipById[memberId]
      delete state.membersByGroupId[groupId][memberId]
    })
    .addCase(fetchBrandFanGroups, (state) => {
      state.isFetchingBrandFanGroups = true
    })
    .addCase(fetchBrandFanGroupsSuccess, (state, action) => {
      const { brandFanGroups, metadata = {}, links, isNext } = action.payload
      state.isFetchingBrandFanGroups = false
      state.groupsById = returnSecondIfDeepEqual(
        indexById(brandFanGroups, state.groupsById),
        state.groupsById,
      )
      state.linksByFilter.groups = returnSecondIfDeepEqual(links, state.linksByFilter.groups)
      const groupIds = brandFanGroups.map((group) => group.id)
      state.groupIds = isNext
        ? addToSet(state.groupIds, groupIds)
        : returnSecondIfDeepEqual(
            brandFanGroups.map((group) => group.id),
            state.groupIds,
          )
      state.stats = {
        ...state.stats,
        ...metadata['stats'],
      }
    })
    .addCase(fetchBrandFanGroupsError, (state, action) => {
      state.isFetchingIdentities = false
      state.isFetchingBrandFanGroups = false
      state.isUpdatingBrandFanGroup = false
      state.isCreatingBrandFanGroup = false
      state.errors = action.payload
    })
    .addDefaultCase(reducer)
})

export { brandFansReducer }
